import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import {
  PlusIcon,
  MagnifyingGlassIcon,
  FunnelIcon,
  LinkIcon,
  DocumentDuplicateIcon,
  EyeIcon,
  XCircleIcon,
} from '@heroicons/react/24/outline';
import { format } from 'date-fns';
import toast from 'react-hot-toast';
import {
  Button,
  Input,
  Select,
  Card,
  CardBody,
  StatusBadge,
  EmptyState,
  Spinner,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
} from '@/components/ui';
import { paymentLinkApi, getErrorMessage } from '@/lib/api';
import type { PaymentLink, PaymentType } from '@/types';

const statusOptions = [
  { value: '', label: 'All Statuses' },
  { value: 'ACTIVE', label: 'Active' },
  { value: 'PAID', label: 'Paid' },
  { value: 'PARTIALLY_PAID', label: 'Partially Paid' },
  { value: 'EXPIRED', label: 'Expired' },
  { value: 'CANCELLED', label: 'Cancelled' },
];

const paymentTypeOptions = [
  { value: '', label: 'All Types' },
  { value: 'RESERVATION_FEE', label: 'Reservation Fee' },
  { value: 'DOWN_PAYMENT', label: 'Down Payment' },
  { value: 'MONTHLY_AMORTIZATION', label: 'Monthly Amortization' },
  { value: 'SPOT_CASH', label: 'Spot Cash' },
  { value: 'BALANCE_PAYMENT', label: 'Balance Payment' },
  { value: 'OTHER', label: 'Other' },
];

const locationOptions = [
  { value: '', label: 'All Locations' },
  { value: 'Niseko', label: 'Niseko' },
  { value: 'Davao', label: 'Davao' },
  { value: 'Madrid', label: 'Madrid' },
  { value: 'Libis', label: 'Libis' },
];

export function PaymentLinksPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [search, setSearch] = useState('');
  const [status, setStatus] = useState('');
  const [paymentType, setPaymentType] = useState('');
  const [location, setLocation] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [cancelModal, setCancelModal] = useState<PaymentLink | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ['payment-links', { search, status, paymentType, location }],
    queryFn: async () => {
      const response = await paymentLinkApi.list({
        search: search || undefined,
        status: status || undefined,
        paymentType: paymentType || undefined,
        propertyLocation: location || undefined,
        limit: 50,
      });
      return response.data;
    },
  });

  const cancelMutation = useMutation({
    mutationFn: (id: string) => paymentLinkApi.cancel(id),
    onSuccess: () => {
      toast.success('Payment link cancelled');
      queryClient.invalidateQueries({ queryKey: ['payment-links'] });
      setCancelModal(null);
    },
    onError: (error) => {
      toast.error(getErrorMessage(error));
    },
  });

  const copyLink = (link: PaymentLink) => {
    const metadata = link.metadata as { adyenLinkUrl?: string } | null;
    const url = metadata?.adyenLinkUrl || `${window.location.origin}/pay/${link.linkCode}`;
    navigator.clipboard.writeText(url);
    toast.success('Payment link copied to clipboard');
  };

  const formatCurrency = (amount: number, currency = 'PHP') => {
    return new Intl.NumberFormat('en-PH', {
      style: 'currency',
      currency,
    }).format(amount);
  };

  const getPaymentTypeLabel = (type: PaymentType) => {
    const labels: Record<PaymentType, string> = {
      RESERVATION_FEE: 'Reservation',
      DOWN_PAYMENT: 'Down Payment',
      MONTHLY_AMORTIZATION: 'Monthly',
      SPOT_CASH: 'Spot Cash',
      BALANCE_PAYMENT: 'Balance',
      OTHER: 'Other',
    };
    return labels[type];
  };

  return (
    <div className="space-y-6">
      {/* Page header */}
      <div className="flex items-center justify-between">
        <div className="page-header mb-0">
          <h1 className="page-title">Payment Links</h1>
          <p className="page-subtitle">Create and manage payment links for property buyers</p>
        </div>
        <Button
          leftIcon={<PlusIcon className="w-4 h-4" />}
          onClick={() => navigate('/admin/payment-links/new')}
        >
          Create Link
        </Button>
      </div>

      {/* Search and filters */}
      <Card>
        <CardBody>
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1">
              <Input
                placeholder="Search by title, customer, or link code..."
                leftIcon={<MagnifyingGlassIcon className="w-5 h-5" />}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>
            <Button
              variant="outline"
              leftIcon={<FunnelIcon className="w-4 h-4" />}
              onClick={() => setShowFilters(!showFilters)}
            >
              Filters
            </Button>
          </div>

          {showFilters && (
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mt-4 pt-4 border-t border-gray-100">
              <Select
                options={statusOptions}
                value={status}
                onChange={(e) => setStatus(e.target.value)}
                placeholder="Filter by status"
              />
              <Select
                options={paymentTypeOptions}
                value={paymentType}
                onChange={(e) => setPaymentType(e.target.value)}
                placeholder="Filter by type"
              />
              <Select
                options={locationOptions}
                value={location}
                onChange={(e) => setLocation(e.target.value)}
                placeholder="Filter by location"
              />
            </div>
          )}
        </CardBody>
      </Card>

      {/* Payment links list */}
      <Card>
        <CardBody className="p-0">
          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <Spinner size="lg" />
            </div>
          ) : data?.data?.length === 0 ? (
            <EmptyState
              icon={<LinkIcon className="w-12 h-12" />}
              title="No payment links found"
              description="Create your first payment link to start collecting payments"
              action={{
                label: 'Create Payment Link',
                onClick: () => navigate('/admin/payment-links/new'),
              }}
            />
          ) : (
            <div className="overflow-x-auto">
              <table className="table">
                <thead>
                  <tr>
                    <th>Link Details</th>
                    <th>Customer</th>
                    <th>Property</th>
                    <th>Amount</th>
                    <th>Status</th>
                    <th>Created</th>
                    <th className="text-right">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {data?.data?.map((link: PaymentLink) => (
                    <tr key={link.id}>
                      <td>
                        <div className="min-w-[200px]">
                          <p className="font-medium text-gray-900">{link.title}</p>
                          <p className="text-xs text-gray-500 mt-0.5">
                            {link.linkCode} · {getPaymentTypeLabel(link.paymentType)}
                          </p>
                        </div>
                      </td>
                      <td>
                        <div className="min-w-[150px]">
                          <p className="text-sm text-gray-900">
                            {link.customerName || '-'}
                          </p>
                          <p className="text-xs text-gray-500">
                            {link.customerEmail || '-'}
                          </p>
                        </div>
                      </td>
                      <td>
                        <div className="min-w-[100px]">
                          <p className="text-sm text-gray-900">
                            {link.propertyLocation || '-'}
                          </p>
                          <p className="text-xs text-gray-500">
                            {link.unitNumber || '-'}
                          </p>
                        </div>
                      </td>
                      <td>
                        <div className="min-w-[120px]">
                          <p className="text-sm font-medium text-gray-900">
                            {formatCurrency(link.amount, link.currency)}
                          </p>
                          {link.amountPaid > 0 && (
                            <p className="text-xs text-success">
                              Paid: {formatCurrency(link.amountPaid, link.currency)}
                            </p>
                          )}
                        </div>
                      </td>
                      <td>
                        <StatusBadge status={link.status} />
                      </td>
                      <td className="text-sm text-gray-500">
                        {format(new Date(link.createdAt), 'MMM d, yyyy')}
                      </td>
                      <td>
                        <div className="flex items-center justify-end gap-2">
                          <button
                            onClick={() => copyLink(link)}
                            className="p-1.5 text-gray-400 hover:text-gray-600 rounded"
                            title="Copy link"
                          >
                            <DocumentDuplicateIcon className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => navigate(`/admin/payment-links/${link.id}`)}
                            className="p-1.5 text-gray-400 hover:text-gray-600 rounded"
                            title="View details"
                          >
                            <EyeIcon className="w-4 h-4" />
                          </button>
                          {link.status === 'ACTIVE' && (
                            <button
                              onClick={() => setCancelModal(link)}
                              className="p-1.5 text-gray-400 hover:text-error rounded"
                              title="Cancel link"
                            >
                              <XCircleIcon className="w-4 h-4" />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardBody>
      </Card>

      {/* Cancel confirmation modal */}
      <Modal
        isOpen={!!cancelModal}
        onClose={() => setCancelModal(null)}
        size="sm"
      >
        <ModalHeader onClose={() => setCancelModal(null)}>
          Cancel Payment Link
        </ModalHeader>
        <ModalBody>
          <p className="text-sm text-gray-600">
            Are you sure you want to cancel the payment link{' '}
            <span className="font-medium">"{cancelModal?.title}"</span>? This
            action cannot be undone.
          </p>
        </ModalBody>
        <ModalFooter>
          <Button variant="outline" onClick={() => setCancelModal(null)}>
            Keep Link
          </Button>
          <Button
            variant="danger"
            isLoading={cancelMutation.isPending}
            onClick={() => cancelModal && cancelMutation.mutate(cancelModal.id)}
          >
            Cancel Link
          </Button>
        </ModalFooter>
      </Modal>
    </div>
  );
}
